// CRM Accounts List Page
import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { QK } from '../../lib/queryKeys';
import { fetchCrmEntities, fetchTenantUsers } from '../../lib/queryFunctions';
import { useAuth } from '../../context/AuthContext';
import OnboardingBeacon from '../../components/onboarding/OnboardingBeacon';
import SidebarLayout from '../../components/layout/SidebarLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import api from '../../services/api';
import {
  Plus,
  Search,
  Building2,
  User,
  ChevronRight,
  Bell,
} from 'lucide-react';
import { TableToolbar } from '../../components/common/TableToolbar';

const ratingColors = {
  A: 'bg-green-100 text-green-800',
  B: 'bg-blue-100 text-blue-800',
  C: 'bg-yellow-100 text-yellow-800',
  D: 'bg-orange-100 text-orange-800',
  F: 'bg-red-100 text-red-800',
  unrated: 'bg-gray-100 text-gray-600',
};

const ACCOUNTS_PARAMS = { crm_class: 'account', include_archived: true };

const AccountsListPage = () => {
  const { isViewOnly, isTenantAdmin, isSystemOwner, user } = useAuth();
  const viewOnly = isViewOnly();
  const isAdmin = isTenantAdmin() || isSystemOwner();
  const navigate = useNavigate();
  const qc = useQueryClient();

  // ── Server state ──────────────────────────────────────────────────────────
  const { data: accounts = [], isLoading } = useQuery({
    queryKey: QK.crmEntities(ACCOUNTS_PARAMS),
    queryFn: fetchCrmEntities(ACCOUNTS_PARAMS),
    onError: () => toast.error('Failed to load accounts'),
  });

  const { data: usersData = [] } = useQuery({
    queryKey: QK.tenantUsers(),
    queryFn: fetchTenantUsers(),
  });

  const { data: alertPrefsData } = useQuery({
    queryKey: QK.alertPrefs(user?.tenant_id),
    queryFn: () => api.get('/tenant-alert-prefs').then(r => r.data),
  });

  // ── Derived from server state ─────────────────────────────────────────────
  const users = useMemo(() => {
    const map = {};
    usersData.forEach(u => { map[u.id] = u.full_name || `${u.first_name} ${u.last_name}`; });
    return map;
  }, [usersData]);

  const managerOptions = useMemo(() =>
    usersData.map(u => ({ id: u.id, name: u.full_name || `${u.first_name} ${u.last_name}` })),
  [usersData]);

  const roleOptions = useMemo(() => {
    const roles = new Set();
    accounts.forEach(a => {
      const role = a.organization_industry_role || a.person_industry_role;
      if (role) roles.add(role);
    });
    return Array.from(roles).sort();
  }, [accounts]);

  const subscribedIds = useMemo(() =>
    new Set(alertPrefsData?.subscribed_account_ids || []),
  [alertPrefsData]);

  // ── UI state ──────────────────────────────────────────────────────────────
  const [searchTerm, setSearchTerm] = useState('');
  const [filterManager, setFilterManager] = useState('all');
  const [filterRating, setFilterRating] = useState('all');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [hideArchived, setHideArchived] = useState(true);

  // Alert subscription bulk mode
  const [alertMode, setAlertMode] = useState(false);
  const [selectedForAlert, setSelectedForAlert] = useState(new Set());
  const [bulkWorking, setBulkWorking] = useState(false);

  // ── Derived / filtered ────────────────────────────────────────────────────
  const getSortKey = (entity) => {
    if (entity.crm_type === 'person') {
      const last = entity.person_last_name || '';
      const first = entity.person_first_name || '';
      return last ? `${last} ${first}`.toLowerCase() : (entity.system_name || '').toLowerCase();
    }
    return (entity.system_name || '').toLowerCase();
  };

  const filteredAccounts = useMemo(() => accounts.filter((account) => {
    const matchesSearch =
      searchTerm === '' ||
      account.system_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      account.organization_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      account.person_full_name?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesManager  = filterManager === 'all' || account.account_manager_id === filterManager;
    const matchesRating   = filterRating === 'all' || account.account_rating === filterRating;
    const accountRole     = account.organization_industry_role || account.person_industry_role;
    const matchesRole     = filterRole === 'all' || accountRole === filterRole;
    const matchesStatus   =
      filterStatus === 'all' ||
      (filterStatus === 'active'   && account.account_active) ||
      (filterStatus === 'inactive' && !account.account_active);
    const matchesArchived = !hideArchived || !account.archived;

    return matchesSearch && matchesManager && matchesRating && matchesRole &&
           matchesStatus && matchesArchived && !account.is_tenant_managed;
  }).sort((a, b) => getSortKey(a).localeCompare(getSortKey(b))),
  [accounts, searchTerm, filterManager, filterRating, filterRole, filterStatus, hideArchived]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Alert mutation helpers ────────────────────────────────────────────────
  const handleBulkAlertAction = async (action) => {
    setBulkWorking(true);
    try {
      await api.post('/tenant-alert-prefs/bulk-subscribe', {
        account_ids: [...selectedForAlert], action,
      });
      qc.invalidateQueries({ queryKey: QK.alertPrefs(user?.tenant_id) });
      const count = selectedForAlert.size;
      const label = `${count} account${count !== 1 ? 's' : ''}`;
      toast.success(action === 'add' ? `Subscribed to ${label}` : `Unsubscribed from ${label}`);
      setSelectedForAlert(new Set());
    } catch {
      toast.error(action === 'add' ? 'Failed to subscribe' : 'Failed to unsubscribe');
    }
    setBulkWorking(false);
  };

  // ── Loading skeleton ──────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <SidebarLayout>
        <div className="space-y-6">
          <Skeleton className="h-10 w-48" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-96 w-full" />
        </div>
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout>
      <div className="space-y-6" data-testid="accounts-list-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-serif text-3xl font-semibold">Accounts</h1>
            <p className="text-muted-foreground mt-1">All Accounts</p>
          </div>
          <div className="flex flex-col gap-2 items-end">
            {isAdmin && accounts.some(a => a.archived) && (
              <Button
                variant={hideArchived ? 'outline' : 'secondary'}
                size="sm"
                className="h-8 text-xs gap-1.5"
                onClick={() => setHideArchived(h => !h)}
                data-testid="toggle-archived-accounts"
              >
                {hideArchived ? 'Show Archived' : 'Hide Archived'}
              </Button>
            )}
            {!viewOnly && (
              <OnboardingBeacon itemKey="add_contact">
                <Button
                  onClick={() => navigate('/crm/accounts/new')}
                  className="btn-primary w-full sm:w-auto"
                  data-testid="new-account-btn"
                >
                  <Plus className="w-4 h-4 mr-2" /> New Account
                </Button>
              </OnboardingBeacon>
            )}
            <Button
              variant="outline"
              size="sm"
              className={`w-full sm:w-auto ${alertMode ? 'border-primary text-primary bg-green-50' : ''}`}
              onClick={() => { setAlertMode(!alertMode); setSelectedForAlert(new Set()); }}
              title="Manage alert subscriptions for accounts"
            >
              <Bell className="w-4 h-4 mr-2" /> Alert Subscriptions
            </Button>
          </div>
        </div>

        {/* Alert bulk-select action bar */}
        {alertMode && (
          <div className="flex items-center gap-3 p-3 bg-primary/5 border border-primary/20 rounded-lg">
            <Bell className="w-4 h-4 text-primary shrink-0" />
            <span className="text-sm font-medium text-primary">{selectedForAlert.size} selected</span>
            <div className="flex gap-2 ml-auto">
              <Button size="sm" variant="outline" className="h-7 text-xs"
                onClick={() => setSelectedForAlert(new Set(filteredAccounts.map(a => a.id)))}>
                Select All
              </Button>
              <Button size="sm" variant="outline" className="h-7 text-xs"
                onClick={() => setSelectedForAlert(new Set())}>
                Clear
              </Button>
              <Button
                size="sm"
                className="h-7 text-xs bg-primary hover:bg-primary/90 text-white"
                disabled={bulkWorking || selectedForAlert.size === 0}
                onClick={() => handleBulkAlertAction('add')}
              >
                Subscribe
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs border-destructive text-destructive hover:bg-destructive/10"
                disabled={bulkWorking || selectedForAlert.size === 0}
                onClick={() => handleBulkAlertAction('remove')}
              >
                Unsubscribe
              </Button>
              <Button size="sm" variant="ghost" className="h-7 text-xs"
                onClick={() => { setAlertMode(false); setSelectedForAlert(new Set()); }}>
                Done
              </Button>
            </div>
          </div>
        )}

        <TableToolbar
          tableId="accounts-list"
          data={filteredAccounts}
          filename="accounts"
          columns={[
            { key: 'system_name', label: 'Account' },
            { key: 'organization_industry_role', label: 'Industry Role' },
            { key: 'account_rating', label: 'Rating' },
            { key: 'account_manager_name', label: 'Manager' },
          ]}
        />

        {/* Table with Integrated Filters */}
        <Card>
          <Table className="table-heritage" data-table-id="accounts-list">
            <TableHeader>
              <TableRow>
                {/* Account Column */}
                <TableHead className="min-w-[250px] align-top bg-muted/20">
                  <div className="flex flex-col gap-2 py-2">
                    <span className="font-bold text-foreground text-sm uppercase">Account</span>
                    <div className="relative">
                      <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
                      <Input
                        placeholder="Search..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="h-8 pl-8 text-xs bg-white"
                        data-testid="search-accounts-input"
                      />
                    </div>
                  </div>
                </TableHead>

                {/* Account Manager Column */}
                <TableHead className="min-w-[200px] align-top bg-muted/20">
                  <div className="flex flex-col gap-2 py-2">
                    <span className="font-bold text-foreground text-sm uppercase">Account Manager</span>
                    <Select value={filterManager} onValueChange={setFilterManager}>
                      <SelectTrigger className="h-8 text-xs bg-white" data-testid="filter-manager-select">
                        <SelectValue placeholder="All" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        {managerOptions.map((manager) => (
                          <SelectItem key={manager.id} value={manager.id}>{manager.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </TableHead>

                {/* Rating Column */}
                <TableHead className="min-w-[120px] align-top bg-muted/20">
                  <div className="flex flex-col gap-2 py-2">
                    <span className="font-bold text-foreground text-sm uppercase">Rating</span>
                    <Select value={filterRating} onValueChange={setFilterRating}>
                      <SelectTrigger className="h-8 text-xs bg-white" data-testid="filter-rating-select">
                        <SelectValue placeholder="All" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="A">A</SelectItem>
                        <SelectItem value="B">B</SelectItem>
                        <SelectItem value="C">C</SelectItem>
                        <SelectItem value="D">D</SelectItem>
                        <SelectItem value="F">F</SelectItem>
                        <SelectItem value="unrated">Unrated</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </TableHead>

                {/* Industry Role Column */}
                <TableHead className="min-w-[180px] align-top bg-muted/20">
                  <div className="flex flex-col gap-2 py-2">
                    <span className="font-bold text-foreground text-sm uppercase">Industry Role</span>
                    <Select value={filterRole} onValueChange={setFilterRole}>
                      <SelectTrigger className="h-8 text-xs bg-white" data-testid="filter-role-select">
                        <SelectValue placeholder="All" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        {roleOptions.map((role) => (
                          <SelectItem key={role} value={role}>
                            {role.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </TableHead>

                {/* Status Column */}
                <TableHead className="min-w-[120px] align-top bg-muted/20">
                  <div className="flex flex-col gap-2 py-2">
                    <span className="font-bold text-foreground text-sm uppercase">Status</span>
                    <Select value={filterStatus} onValueChange={setFilterStatus}>
                      <SelectTrigger className="h-8 text-xs bg-white" data-testid="filter-status-select">
                        <SelectValue placeholder="All" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </TableHead>

                <TableHead className="w-[50px] bg-muted/20"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredAccounts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-12">
                    <Building2 className="w-12 h-12 mx-auto mb-3 text-muted-foreground/20" />
                    <p className="text-muted-foreground">No accounts found</p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-4"
                      onClick={() => navigate('/crm/accounts/new')}
                    >
                      <Plus className="w-4 h-4 mr-2" /> Create First Account
                    </Button>
                  </TableCell>
                </TableRow>
              ) : (
                filteredAccounts.map((account) => (
                  <TableRow
                    key={account.id}
                    className="cursor-pointer"
                    onClick={() => alertMode
                      ? setSelectedForAlert(prev => {
                          const n = new Set(prev);
                          n.has(account.id) ? n.delete(account.id) : n.add(account.id);
                          return n;
                        })
                      : navigate(`/crm/accounts/${account.id}`)
                    }
                    data-testid={`account-row-${account.id}`}
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        {alertMode && (
                          <input type="checkbox" readOnly className="shrink-0 accent-primary"
                            checked={selectedForAlert.has(account.id)} />
                        )}
                        <div className="w-10 h-10 rounded-full overflow-hidden bg-secondary flex items-center justify-center shrink-0">
                          {account.main_photo_url ? (
                            <img
                              src={account.main_photo_url.startsWith('http')
                                ? account.main_photo_url
                                : `${window.location.origin}${account.main_photo_url}`}
                              alt={account.system_name}
                              className="w-full h-full object-cover"
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          ) : account.crm_type === 'organization' ? (
                            <Building2 className="w-5 h-5 text-muted-foreground" />
                          ) : (
                            <User className="w-5 h-5 text-muted-foreground" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium flex items-center gap-1.5">
                            {account.system_name}
                            {account.archived && <Badge variant="outline" className="ml-1 border-orange-400 text-orange-600 text-[10px]">Archived</Badge>}
                            {subscribedIds.has(account.id) && <Bell className="w-3 h-3 text-primary fill-primary" />}
                          </p>
                          {account.crm_type === 'person' && account.organization_name && (
                            <p className="text-xs text-muted-foreground">{account.organization_name}</p>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">{users[account.account_manager_id] || '-'}</span>
                    </TableCell>
                    <TableCell>
                      <Badge className={`${ratingColors[account.account_rating || 'unrated']} font-semibold`}>
                        {account.account_rating?.toUpperCase() || 'N/A'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm capitalize">
                        {account.organization_industry_role?.replace(/_/g, ' ') ||
                          account.person_industry_role?.replace(/_/g, ' ') ||
                          '-'}
                      </span>
                    </TableCell>
                    <TableCell>
                      {account.account_active ? (
                        <Badge className="badge-active">Active</Badge>
                      ) : (
                        <Badge variant="outline">Inactive</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Card>
      </div>
    </SidebarLayout>
  );
};

export default AccountsListPage;
